import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  HStack,
  VStack,
  Icon,
  Button,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Divider,
  useToast
} from '@chakra-ui/react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiClock,
  FiUsers,
  FiMonitor,
  FiMapPin,
  FiActivity,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiBarChart2,
  FiPieChart
} from 'react-icons/fi';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { useAuthStore } from '@/stores/authStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useTranslation } from "react-i18next";
import apiClient from '@/services/letsparkyClient';
import { durationToString } from '@/utils/utils';


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

interface AnalyticsData {
  parkingSessions: any[];
  devices: any[];
  payments: any[];
  stats: any;
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  variant?: 'primary' | 'success' | 'warning' | 'danger';
  trend?: {
    value: string;
    isPositive: boolean;
  };
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  variant = 'primary',
  trend
}) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const variantColors = {
    primary: { bg: 'blue.500', accent: 'blue.100' },
    success: { bg: 'green.500', accent: 'green.100' },
    warning: { bg: 'orange.500', accent: 'orange.100' },
    danger: { bg: 'red.500', accent: 'red.100' }
  };

  const colors = variantColors[variant];

  return (
    <Card
      bg={bg}
      borderColor={borderColor}
      borderWidth="1px"
      borderRadius="xl"
      shadow="sm"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        height="4px"
        bgGradient={`linear(to-r, ${colors.bg}, ${colors.bg})`}
      />
      
      <CardBody p={6}>
        <Flex justify="space-between" align="flex-start" mb={4}>
          <VStack align="flex-start" spacing={2} flex="1">
            <HStack spacing={3}>
              <Box
                p={3}
                borderRadius="lg"
                bg={colors.accent}
                fontSize="24px"
              >
                {icon}
              </Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                {title}
              </Text>
            </HStack>
            
            <Text fontSize="3xl" fontWeight="bold" lineHeight="1">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Text>
            
            {subtitle && (
              <Text fontSize="sm" color="gray.500">
                {subtitle}
              </Text>
            )}
            
            {trend && (
              <HStack spacing={1}>
                <Icon 
                  as={trend.isPositive ? FiTrendingUp : FiTrendingDown}
                  color={trend.isPositive ? 'green.500' : 'red.500'}
                  boxSize={4}
                />
                <Text 
                  fontSize="sm" 
                  color={trend.isPositive ? 'green.500' : 'red.500'}
                  fontWeight="medium"
                >
                  {trend.value}
                </Text>
              </HStack>
            )}
          </VStack>
        </Flex>
      </CardBody>
    </Card>
  );
};

const InsightCard: React.FC<{
  title: string;
  description: string;
  type: 'success' | 'warning' | 'danger' | 'info';
  actions?: string[];
}> = ({ title, description, type, actions }) => {
  const typeColors = {
    success: { border: 'green.500', bg: 'green.50', text: 'green.700' },
    warning: { border: 'orange.500', bg: 'orange.50', text: 'orange.700' },
    danger: { border: 'red.500', bg: 'red.50', text: 'red.700' },
    info: { border: 'blue.500', bg: 'blue.50', text: 'blue.700' }
  };

  const colors = typeColors[type];

  return (
    <Box
      p={4}
      borderRadius="lg"
      bg={colors.bg}
      borderLeft="4px solid"
      borderLeftColor={colors.border}
    >
      <Heading size="sm" color={colors.text} mb={2}>
        {title}
      </Heading>
      <Text fontSize="sm" mb={3}>
        {description}
      </Text>
      {actions && (
        <VStack align="flex-start" spacing={1}>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">
            Recommended Actions:
          </Text>
          {actions.map((action, index) => (
            <Text key={index} fontSize="xs" color="gray.600">
              • {action}
            </Text>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export const AnalyticsReports: React.FC = () => {
  const { user, token } = useAuthStore();
  const { stats, parkingSessions, devices, fetchStats, fetchDevices, fetchParkingSessions } = useDashboardStore();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const toast = useToast();
  const { t } = useTranslation();

  const fetchAnalyticsData = async () => {
    if (!token || !user) return;

    try {
      setIsLoading(true);
      apiClient.client.setToken(token);

      await fetchDevices();

      // Fetch comprehensive data
      const [parkingsResponse, paymentsResponse] = await Promise.all([
        apiClient.parkings.listPaginated({ page: 1, limit: 1000 }),
        apiClient.payments.listPaginated({ page: 1, limit: 1000 })
      ]);

      const isSystem = user.role === 'SYSTEM';
      
      await Promise.all([
        fetchStats(isSystem, user.tenantId),
        fetchParkingSessions('ALL')
      ]);

      setAnalyticsData({
        parkingSessions: parkingsResponse.data || [],
        devices: devices || [],
        payments: paymentsResponse.data || [],
        stats: stats || {}
      });

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast({
        title: 'Data loading error',
        description: 'An error occurred while loading analytics data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [user, token]);

  // Calculate analytics metrics
  const calculateMetrics = () => {
    if (!analyticsData) return null;

    const { parkingSessions, devices, payments } = analyticsData;

    // Status distribution
    const statusStats = parkingSessions.reduce((acc: any, session: any) => {
      acc[session.status] = (acc[session.status] || 0) + 1;
      return acc;
    }, {});

    // Duration analysis
    const durationStats = parkingSessions
      .filter((session: any) => session.start && session.end)
      .map((session: any) => {
        const start = new Date(session.start);
        const end = new Date(session.end);
        return (end.getTime() - start.getTime()) / 1000; // seconds
      });

    const avgDuration = durationStats.length > 0 
      ? Math.floor(durationStats.reduce((a, b) => a + b, 0) / durationStats.length)
      : 0;

    // Revenue analysis
    const totalRevenue = payments.reduce((sum: number, payment: any) => 
      sum + (payment.amount || 0), 0);

    // Time-based analysis
    const hourlyStats = parkingSessions.reduce((acc: any, session: any) => {
      if (session.start) {
        const hour = new Date(session.start).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
      }
      return acc;
    }, {});

    // Device parking distribution
    const deviceStats = parkingSessions.reduce((acc: any, session: any) => {
        // Find the device for this session
      //   console.log(session, devices)
        const device = useDashboardStore.getState().devices.find((d: any) => d.id === session.device_id);
        if (!device) {
          // Handle sessions with unknown devices
          acc['Unknown Device'] = (acc['Unknown Device'] || 0) + 1;
          return acc;
        }
        
        const deviceName = device.nick_name || device.serial_no || `Device ${device.id}`;
        acc[deviceName] = (acc[deviceName] || 0) + 1;
        return acc;
      }, {});

    return {
      deviceStats,
      statusStats,
      durationStats,
      avgDuration,
      totalRevenue,
      hourlyStats,
      totalSessions: parkingSessions.length,
      activeSessions: statusStats.ACTIVE || 0,
      completedSessions: statusStats.COMPLETED || 0
    };
  };

  const metrics = calculateMetrics();

  if (isLoading || !metrics) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">{t('dashboard.loadingText')}</Text>
      </Box>
    );
  }

  // Chart configurations
  const deviceChartData = {
    labels: Object.keys(metrics.deviceStats),
    datasets: [{
      data: Object.values(metrics.deviceStats),
      backgroundColor: [
        '#e74c3c', '#3498db', '#27ae60', '#f39c12', '#9b59b6',
        '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#2ecc71'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const statusChartData = {
    labels: Object.keys(metrics.statusStats),
    datasets: [{
      data: Object.values(metrics.statusStats),
      backgroundColor: ['#27ae60', '#f39c12', '#3498db', '#e74c3c'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const hourlyChartData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [{
      label: 'Parking Count',
      data: Array.from({ length: 24 }, (_, i) => metrics.hourlyStats[i] || 0),
      backgroundColor: 'rgba(52, 152, 219, 0.2)',
      borderColor: 'rgba(52, 152, 219, 1)',
      borderWidth: 2,
      fill: true
    }]
  };

  const durationDistributionData = {
    labels: ['0-2 Hours', '2-6 Hours', '6-12 Hours', '12+ Hours'],
    datasets: [{
      label: 'Parking Count',
      data: [
        metrics.durationStats.filter((d: number) => d <= 2 * 60 * 60).length,
        metrics.durationStats.filter((d: number) => d > 2 * 60 * 60 && d <= 6 * 60 * 60).length,
        metrics.durationStats.filter((d: number) => d > 6 * 60 * 60 && d <= 12 * 60 * 60).length,
        metrics.durationStats.filter((d: number) => d > 12 * 60 * 60).length
      ],
      backgroundColor: ['#3498db', '#27ae60', '#f39c12', '#e74c3c'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true
        }
      }
    }
  };

  console.log(metrics.durationStats)

  return (
    <Box p={6} maxW="1400px" mx="auto">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="xl" mb={2}>
            📊 {t('dashboard.analytics.title')}
          </Heading>
          <Text color="gray.600">
            {t('dashboard.analytics.subtitle')}
          </Text>
        </Box>
        <VStack align="flex-end" spacing={2}>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchAnalyticsData}
            isLoading={isLoading}
            loadingText={t('dashboard.analytics.refreshing')}
            colorScheme="blue"
            variant="outline"
            size="sm"
          >
            {t('dashboard.analytics.refresh')}
          </Button>
          <Text fontSize="xs" color="gray.500">
            {t('dashboard.analytics.lastUpdated')}: {lastRefresh.toLocaleTimeString()}
          </Text>
        </VStack>
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList mb={6}>
          <Tab>📈 {t('dashboard.analytics.tabs.executiveSummary')}</Tab>
          <Tab>⏰ {t('dashboard.analytics.tabs.timeAnalysis')}</Tab>
          <Tab>⏱️ {t('dashboard.analytics.tabs.durationAnalysis')}</Tab>
        </TabList>

        <TabPanels>
          {/* Executive Summary */}
          <TabPanel p={0}>
            <VStack spacing={8} align="stretch">
              {/* KPI Grid */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <KPICard
                  title={t('dashboard.analytics.kpi.totalParkings')}
                  value={metrics.totalSessions}
                  subtitle={t('dashboard.analytics.kpi.allParkings')}
                  icon="🚗"
                  variant="primary"
                //   trend={{ value: '+12%', isPositive: true }}
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.activeParking')}
                  value={metrics.activeSessions}
                  subtitle={t('dashboard.analytics.kpi.ongoingParkings')}
                  icon="🟢"
                  variant="success"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.totalRevenue')}
                  value={`₺${metrics.totalRevenue.toLocaleString()} / ₺${Math.floor(metrics.durationStats.reduce((a, b) => a + b, 0)/(60 * 60)) * 70}`}
                  subtitle={t('dashboard.analytics.kpi.allPayments')}
                  icon="💰"
                  variant="warning"
                //   trend={{ value: '+8.5%', isPositive: true }}
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.averageDuration')}
                  value={durationToString(metrics.avgDuration)}
                  subtitle={t('dashboard.analytics.kpi.parkingDuration')}
                  icon="⏱️"
                  variant="danger"
                />
              </SimpleGrid>

              {/* Charts Grid */}
              <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={8}>
                <Card>
                  <CardHeader>
                    <Heading size="md">{t('dashboard.analytics.charts.parkingsPerDevice')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <Box height="300px">
                      <Doughnut data={deviceChartData} options={chartOptions} />
                    </Box>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <Heading size="md">{t('dashboard.analytics.charts.parkingStatus')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <Box height="300px">
                      <Pie data={statusChartData} options={chartOptions} />
                    </Box>
                  </CardBody>
                </Card>
              </Grid>

              {/* Device Performance Table */}
              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.deviceUsage')}</Heading>
                </CardHeader>
                <CardBody>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('dashboard.analytics.charts.deviceName')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.numberOfParkings')}</Th>
                          <Th>{t('dashboard.analytics.charts.status')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.parkingShare')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {Object.entries(metrics.deviceStats).map(([device, count]: [string, any]) => (
                          <Tr key={device}>
                            <Td fontWeight="medium">{device}</Td>
                            <Td isNumeric>{count}</Td>
                            <Td>
                              <Badge colorScheme="green" size="sm">{t('common.active')}</Badge>
                            </Td>
                            <Td isNumeric>
                              {((count / metrics.totalSessions) * 100).toFixed(1)}%
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* Timeline Analysis */}
          <TabPanel p={0}>
            <VStack spacing={8} align="stretch">
              {/* Hourly Distribution Chart */}
              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.hourlyParkings')}</Heading>
                </CardHeader>
                <CardBody>
                  <Box height="400px">
                    <Line data={hourlyChartData} options={{
                      ...chartOptions,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: t('dashboard.analytics.charts.transactionCount')
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: t('dashboard.analytics.charts.hour')
                          }
                        }
                      }
                    }} />
                  </Box>
                </CardBody>
              </Card>

              {/* Peak Hours Analysis */}
              <Card>
                <CardHeader>
                  <Heading size="md">🕐 {t('dashboard.analytics.charts.hourlyTrafficAnalysis')}</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                    {/* Calculate peak hours - hours with highest transactions */}
                    {(() => {
                      const hourlyEntries = Object.entries(metrics.hourlyStats)
                        .map(([hour, count]) => ({ hour: parseInt(hour), count: (count as number) || 0 }))
                        .sort((a, b) => b.count - a.count);
                      
                      const peakHours = hourlyEntries.slice(0, 3);
                      const peakHoursStr = peakHours
                        .map(h => `${h.hour.toString().padStart(2, '0')}:00`)
                        .join(', ');
                      const peakTotal = peakHours.reduce((sum, h) => sum + h.count, 0);

                      return (
                        <Box p={6} borderRadius="lg" bg="red.50" borderLeft="4px solid" borderLeftColor="red.500">
                          <Heading size="sm" color="red.700" mb={2}>🔥 {t('dashboard.analytics.charts.peakHours')}</Heading>
                          <Text fontWeight="bold">{peakHoursStr}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {peakTotal} {t('dashboard.analytics.charts.transactions')}
                          </Text>
                        </Box>
                      );
                    })()}

                    {/* Calculate busy hours - hours with moderate transactions */}
                    {(() => {
                      const hourlyEntries = Object.entries(metrics.hourlyStats)
                        .map(([hour, count]) => ({ hour: parseInt(hour), count: (count as number) || 0 }))
                        .sort((a, b) => b.count - a.count);
                      
                      const busyHours = hourlyEntries.slice(3, 6);
                      const busyHoursStr = busyHours
                        .map(h => `${h.hour.toString().padStart(2, '0')}:00`)
                        .join(', ');
                      const busyTotal = busyHours.reduce((sum, h) => sum + h.count, 0);

                      return (
                        <Box p={6} borderRadius="lg" bg="orange.50" borderLeft="4px solid" borderLeftColor="orange.500">
                          <Heading size="sm" color="orange.700" mb={2}>⚡ {t('dashboard.analytics.charts.busyHours')}</Heading>
                          <Text fontWeight="bold">{busyHoursStr}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {busyTotal} {t('dashboard.analytics.charts.transactions')}
                          </Text>
                        </Box>
                      );
                    })()}

                    {/* Calculate business hours - hours during typical work day */}
                    {(() => {
                      const businessHours = Array.from({ length: 9 }, (_, i) => i + 9); // 9AM to 5PM
                      const businessTotal = businessHours.reduce((sum, hour) => 
                        sum + (metrics.hourlyStats[hour] || 0), 0);
                      
                      return (
                        <Box p={6} borderRadius="lg" bg="green.50" borderLeft="4px solid" borderLeftColor="green.500">
                          <Heading size="sm" color="green.700" mb={2}>💼 {t('dashboard.analytics.charts.businessHours')}</Heading>
                          <Text fontWeight="bold">09:00 - 17:00</Text>
                          <Text fontSize="sm" color="gray.600">
                            {businessTotal} {t('dashboard.analytics.charts.transactions')}
                          </Text>
                        </Box>
                      );
                    })()}

                    {/* Calculate inactive hours - hours with lowest transactions */}
                    {(() => {
                      const hourlyEntries = Object.entries(metrics.hourlyStats)
                        .map(([hour, count]) => ({ hour: parseInt(hour), count: (count as number) || 0 }))
                        .sort((a, b) => a.count - b.count);
                      
                      const inactiveHours = hourlyEntries.slice(0, 3);
                      const inactiveHoursStr = inactiveHours
                        .map(h => `${h.hour.toString().padStart(2, '0')}:00`)
                        .join(', ');
                      const inactiveTotal = inactiveHours.reduce((sum, h) => sum + h.count, 0);

                      return (
                        <Box p={6} borderRadius="lg" bg="blue.50" borderLeft="4px solid" borderLeftColor="blue.500">
                          <Heading size="sm" color="blue.700" mb={2}>🌙 {t('dashboard.analytics.charts.inactiveHours')}</Heading>
                          <Text fontWeight="bold">{inactiveHoursStr}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {inactiveTotal} {t('dashboard.analytics.charts.transactions')}
                          </Text>
                        </Box>
                      );
                    })()}
                  </SimpleGrid>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* Duration Analysis */}
          <TabPanel p={0}>
            <VStack spacing={8} align="stretch">
              {/* Duration KPIs */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <KPICard
                  title={t('dashboard.analytics.kpi.averageDuration')}
                  value={durationToString(metrics.avgDuration)}
                  subtitle={t('dashboard.analytics.kpi.optimalLevel')}
                  icon="⏱️"
                  variant="primary"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.longestParking')}
                  value={durationToString(Math.max(...metrics.durationStats))}
                  subtitle={t('dashboard.analytics.kpi.maximumDuration')}
                  icon="🏆"
                  variant="success"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.shortestParking')}
                  value={durationToString(Math.min(...metrics.durationStats))}
                  subtitle={t('dashboard.analytics.kpi.minimumDuration')}
                  icon="⚡"
                  variant="warning"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.totalParkingHours')}
                  value={durationToString(Math.floor(metrics.durationStats.reduce((a, b) => a + b, 0)))}
                  subtitle={t('dashboard.analytics.kpi.cumulativeDuration')}
                  icon="📊"
                  variant="danger"
                />
              </SimpleGrid>

              {/* Duration Distribution Chart */}
              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.parkingDuration')}</Heading>
                </CardHeader>
                <CardBody>
                  <Box height="400px">
                    <Bar data={{
                      labels: [
                        t('dashboard.analytics.charts.shortTermParking'),
                        t('dashboard.analytics.charts.mediumTermParking'),
                        t('dashboard.analytics.charts.longTermParking'),
                        t('dashboard.analytics.charts.extraLongParking')
                      ],
                      datasets: durationDistributionData.datasets
                    }} options={{
                      ...chartOptions,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: t('dashboard.analytics.charts.transactionCount')
                          }
                        }
                      }
                    }} />
                  </Box>
                </CardBody>
              </Card>

              {/* Duration Analysis Table */}
              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.durationAnalysis')}</Heading>
                </CardHeader>
                <CardBody>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('dashboard.analytics.charts.durationRange')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.transactionCount')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.totalDuration')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.revenue')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.parkingNumberShare')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td><strong>0-2 {t('common.time')}</strong><br /><Text fontSize="sm" color="gray.500">{t('dashboard.analytics.charts.shortTermParking')}</Text></Td>
                          <Td isNumeric>{metrics.durationStats.filter((d: number) => d <= 2 * 60 * 60).length}</Td>
                          <Td isNumeric>{durationToString(Math.floor(metrics.durationStats.filter((d: number) => d <= 2 * 60 * 60).reduce((a, b) => a + b, 0)))}</Td>
                          <Td isNumeric>{Math.round(70 * (metrics.durationStats.filter((d: number) => d <= 2 * 60 * 60).reduce((a, b) => a + b, 0) / (60 * 60)) / 10)*10}TL</Td>
                          <Td isNumeric>{((metrics.durationStats.filter((d: number) => d <= 2 * 60 * 60).length / metrics.durationStats.length) * 100).toFixed(1)}%</Td>
                        </Tr>
                        <Tr>
                          <Td><strong>2-6 {t('common.time')}</strong><br /><Text fontSize="sm" color="gray.500">{t('dashboard.analytics.charts.mediumTermParking')}</Text></Td>
                          <Td isNumeric>{metrics.durationStats.filter((d: number) => d > 2 * 60 * 60 && d <= 6 * 60 * 60).length}</Td>
                          <Td isNumeric>{durationToString(Math.floor(metrics.durationStats.filter((d: number) => d > 2 * 60 * 60 && d <= 6 * 60 * 60).reduce((a, b) => a + b, 0)))}</Td>
                          <Td isNumeric>{Math.round(70 * (metrics.durationStats.filter((d: number) => d > 2 * 60 * 60 && d <= 6 * 60 * 60).reduce((a, b) => a + b, 0) / (60 * 60)))}TL</Td>
                          <Td isNumeric>{((metrics.durationStats.filter((d: number) => d > 2 * 60 * 60 && d <= 6 * 60 * 60).length / metrics.durationStats.length) * 100).toFixed(1)}%</Td>
                        </Tr>
                        <Tr>
                          <Td><strong>6-12 {t('common.time')}</strong><br /><Text fontSize="sm" color="gray.500">{t('dashboard.analytics.charts.longTermParking')}</Text></Td>
                          <Td isNumeric>{metrics.durationStats.filter((d: number) => d > 6 * 60 * 60 && d <= 12 * 60 * 60).length}</Td>
                          <Td isNumeric>{durationToString(Math.floor(metrics.durationStats.filter((d: number) => d > 6 * 60 * 60 && d <= 12 * 60 * 60).reduce((a, b) => a + b, 0)))}</Td>
                          <Td isNumeric>{Math.round(70 * (metrics.durationStats.filter((d: number) => d > 6 * 60 * 60 && d <= 12 * 60 * 60).reduce((a, b) => a + b, 0) / (60 * 60)))}TL</Td>
                          <Td isNumeric>{((metrics.durationStats.filter((d: number) => d > 6 * 60 * 60 && d <= 12 * 60 * 60).length / metrics.durationStats.length) * 100).toFixed(1)}%</Td>
                        </Tr>
                        <Tr>
                          <Td><strong>12+ {t('common.time')}</strong><br /><Text fontSize="sm" color="gray.500">{t('dashboard.analytics.charts.extraLongParking')}</Text></Td>
                          <Td isNumeric>{metrics.durationStats.filter((d: number) => d > 12 * 60 * 60).length}</Td>
                          <Td isNumeric>{durationToString(Math.floor(metrics.durationStats.filter((d: number) => d > 12 * 60 * 60).reduce((a, b) => a + b, 0)))}</Td>
                          <Td isNumeric>{Math.round(70 * (metrics.durationStats.filter((d: number) => d > 12 * 60 * 60).reduce((a, b) => a + b, 0) / (60 * 60)))}TL</Td>
                          <Td isNumeric>{((metrics.durationStats.filter((d: number) => d > 12 * 60 * 60).length / metrics.durationStats.length) * 100).toFixed(1)}%</Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}; 